import type { GeneratedContentPage } from '#layers/spaces/utils/content-pages';
import { readonly, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { isGeneratedKey, randomUuid } from '#layers/base/public';
import { useWorkspaceEntityStore } from '#layers/runtime/public';
import { usePageNavigation } from '#layers/spaces/composables/use-page-navigation';
import { useWorkspacePagesStore } from '#layers/spaces/stores/workspace-pages';
import { blockRenderDataFactory } from '#layers/spaces/utils/block-render-data-factory';
import { createContentDetailBlockRenderData, createContentPageOverrideInput, getContentPageOverrideId, getGeneratedContentPage } from '#layers/spaces/utils/content-pages';
import { useToastResultHandling } from '#layers/ui/public';

export function useContentPageCustomization() {
	const { t } = useI18n();
	const store = useWorkspaceEntityStore();
	// `usePageNavigation` rather than `usePageActions`: the latter re-exports this same `editPage`
	// but also builds the delete flow and an overlay this composable never uses.
	const { editPage } = usePageNavigation();
	const { fetchOverridePage, refreshPages } = useWorkspacePagesStore();

	const isCustomizing = ref(false);

	const { onError } = useToastResultHandling({
		error: {
			title: t('page.customize_page_error'),
		},
	});

	return {
		customizeContentDetailPage,
		editContentDetailPage,
		isCustomizing: readonly(isCustomizing),
	};

	/**
	 * Opens the page builder on whatever renders this collection's detail view, customizing the
	 * generated page first when there is nothing saved to edit yet.
	 */
	async function editContentDetailPage(
		collectionId: NonNullable<GeneratedContentPage['collectionId']>,
		pageId: string | null | undefined,
	) {
		if (pageId && !isGeneratedKey(pageId)) {
			await editPage(pageId, { saveCurrentPath: true });
			return;
		}

		await customizeContentDetailPage(collectionId);
	}

	async function customizeContentDetailPage(collectionId: NonNullable<GeneratedContentPage['collectionId']>) {
		if (isCustomizing.value) {
			return;
		}

		isCustomizing.value = true;

		try {
			const generatedPage = getGeneratedContentPage(store, {
				collectionId,
				type: 'detail',
				intent: 'edit',
			});

			if (!generatedPage) {
				return;
			}

			const overridePageId = getContentPageOverrideId(generatedPage);

			// `override_page` is unique in the database, so this answer has to come from the server.
			// The cache is not authoritative: rstore garbage-collects the override row once no live
			// query holds it — closing the page builder is enough — and a cache miss read as
			// "no customization exists" makes the next create violate that constraint.
			let customPage = await fetchOverridePage(overridePageId);
			let written = false;

			if (!customPage) {
				customPage = await createOverridePage(generatedPage, overridePageId);
				written = true;
			}

			const customPageBlock = await store.MonospaceBlock.findFirst({
				filter: {
					pageId: { _eq: customPage.id },
				},
				fetchPolicy: 'fetch-only',
			});

			if (!customPageBlock) {
				await store.MonospaceBlock.create({
					id: randomUuid(),
					name: 'Page Block',
					category: 'page',
					pageId: customPage.id,
					renderData: blockRenderDataFactory(createContentDetailBlockRenderData()),
				});

				written = true;
			}

			// RStore populates query tracking when a fetch resolves, not when an item is written,
			// so a row we just created is cached but unowned — and collected the moment the page
			// builder closes. Re-reading hands it to the store's query.
			if (written) {
				await refreshPages();
			}

			await editPage(customPage.id, {
				saveCurrentPath: true,
			});
		}
		catch (error) {
			onError(error);
		}
		finally {
			isCustomizing.value = false;
		}
	}

	async function createOverridePage(generatedPage: GeneratedContentPage, overridePageId: string) {
		try {
			return await store.MonospacePage.create({
				id: randomUuid(),
				...createContentPageOverrideInput(generatedPage),
			});
		}
		catch (error) {
			// `override_page` is unique, so a create can only lose to a row that now exists — from
			// another tab, or a click that raced this one. Ask the server rather than parsing the
			// database error, which carries no structured constraint code.
			const winner = await fetchOverridePage(overridePageId);

			if (!winner) {
				throw error;
			}

			return winner;
		}
	}
}
